import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ResultTable from "../Results/ResultTable";
import buildFinalExamVariantCells from "../Results/buildFinalExamVariantCells";
import StatusBadge from "../Badges/StatusBadge";
import {
  formatDate,
  dateIsAfter,
  compareDatesDesc,
} from "../../Utilities/dateUtils";
import {
  isGy25TestModule,
  roundScore,
  findCoupledTestModule,
  getNbrOfAttemptsToShow,
} from "../../Utilities/moduleHelper";
import { compareByName } from "../../Utilities/sortHelper";
import { groupStudentFinalExamVariants } from "../../Utilities/groupFinalExamVariants";
import { FINAL_EXAM_TYPE } from "../../constants/ConstantVars";

const isGy25FinalExam = (mod) => mod.isGy25 && mod.isFinalExam;

const formatScore = ({ score, maxScore, scoreInPercent }) =>
  `${roundScore(score)}/${maxScore} (${scoreInPercent}%)`;

const formatActivityPoints = (points, maxPoints) => {
  const percentage = maxPoints > 0 ? Math.round((points / maxPoints) * 100) : 0;

  return `${points}/${maxPoints} (${percentage}%)`;
};

const pairModules = (modules) =>
  modules
    .filter((mod) => !(isGy25TestModule(mod) && mod.moduleCoupleId))
    .map((mod) => ({
      mod,
      coupledTestMod: findCoupledTestModule(mod, modules),
    }));

const latestDate = (dates) =>
  dates
    .filter(Boolean)
    .reduce(
      (latest, date) => (!latest || dateIsAfter(date, latest) ? date : latest),
      null,
    );

const getModuleResult = (mod, studentId) => {
  const result = mod.students.find(
    (studentResult) => studentResult.studentId === studentId,
  );

  if (isGy25FinalExam(mod)) {
    const finalExamVariants = result?.finalExamVariants ?? [];

    return {
      status: result?.finalExamStatus ?? "NotStarted",
      activityPoints: 0,
      maxActivityPoints: 0,
      nbrOfAttempts: 0,
      lastRun: latestDate(finalExamVariants.map((variant) => variant.lastRun)),
      finalExamVariants,
    };
  }

  return {
    status: result?.status ?? "NotStarted",
    activityPoints: result?.activityPoints ?? 0,
    maxActivityPoints: result
      ? result.maxActivityPoints
      : mod.maxActivityPoints,
    score: result?.score ?? 0,
    maxScore: result ? result.maxScore : mod.maxScore,
    scoreInPercent: result?.scoreInPercent ?? 0,
    nbrOfAttempts: result?.nbrOfAttempts ?? 0,
    lastRun: result?.lastRun ?? null,
  };
};

const buildFinalExamVariantRows = (finalExamVariants, finalExamRowId, intl) =>
  groupStudentFinalExamVariants(finalExamVariants).flatMap(
    ({ parent, children }) => {
      const parentRowId = finalExamRowId + "exam" + parent.variantId;

      return [
        {
          ...buildFinalExamVariantCells(parent, FINAL_EXAM_TYPE.PARENT, intl),
          id: parentRowId,
          parentId: finalExamRowId,
          nbrOfAttempts: parent.lastRun == null ? "-" : 1,
        },
        ...[...children]
          .sort((a, b) => compareDatesDesc(a.lastRun, b.lastRun))
          .map((child) => ({
            ...buildFinalExamVariantCells(child, FINAL_EXAM_TYPE.CHILD, intl),
            id: parentRowId + "retest" + child.variantId,
            parentId: parentRowId,
            nbrOfAttempts: child.lastRun == null ? "-" : 1,
          })),
      ];
    },
  );

const formatLastRun = (lastRun) =>
  lastRun ? formatDate(lastRun, "PPp") : null;

export default function ClassStudentResultOverview({ course, classStudents }) {
  const intl = useIntl();

  const tableData = useMemo(() => {
    const modulePairs = pairModules(
      course.modules
        .filter((mod) => !mod.isPreview)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    );

    return [...classStudents].sort(compareByName).flatMap((student) => {
      const studentRowId = "student" + student.id;

      const moduleResults = modulePairs.map(({ mod, coupledTestMod }) => {
        const moduleResult = getModuleResult(mod, student.id);
        const testResult = coupledTestMod
          ? getModuleResult(coupledTestMod, student.id)
          : null;

        return { mod, coupledTestMod, moduleResult, testResult };
      });

      const moduleRows = moduleResults.flatMap(
        ({ mod, coupledTestMod, moduleResult, testResult }) => {
          const moduleRowId = studentRowId + "module" + mod.moduleId;

          const moduleRow = {
            id: moduleRowId,
            parentId: studentRowId,
            name: mod.name,
            status: coupledTestMod ? (
              <>
                <StatusBadge
                  status={moduleResult.status}
                  title={intl.formatMessage({ id: "StatusForFactModule" })}
                />
                <StatusBadge
                  status={testResult.status}
                  title={intl.formatMessage({ id: "StatusForTestModule" })}
                  isTestModule={true}
                />
              </>
            ) : (
              <StatusBadge
                status={moduleResult.status}
                isGy25FinalExam={isGy25FinalExam(mod)}
              />
            ),
            score: isGy25FinalExam(mod)
              ? null
              : formatScore(testResult ?? moduleResult),
            nbrOfAttempts: getNbrOfAttemptsToShow(
              moduleResult.nbrOfAttempts,
              testResult?.nbrOfAttempts,
            ),
            activityPoints: isGy25FinalExam(mod)
              ? null
              : formatActivityPoints(
                  moduleResult.activityPoints,
                  moduleResult.maxActivityPoints,
                ),
            lastRun: formatLastRun(
              latestDate([moduleResult.lastRun, testResult?.lastRun]),
            ),
          };

          return isGy25FinalExam(mod)
            ? [
                moduleRow,
                ...buildFinalExamVariantRows(
                  moduleResult.finalExamVariants,
                  moduleRowId,
                  intl,
                ),
              ]
            : [moduleRow];
        },
      );

      const studentCourseSummary = course.students.find(
        (courseStudent) => courseStudent.studentId === student.id,
      );

      const studentRow = {
        id: studentRowId,
        parentId: null,
        name: student.name,
        status: (
          <StatusBadge status={studentCourseSummary?.status ?? "NotStarted"} />
        ),
        donePercentage: studentCourseSummary?.donePercentage ?? 0,
        activityPoints: formatActivityPoints(
          moduleResults.reduce(
            (sum, { moduleResult }) => sum + moduleResult.activityPoints,
            0,
          ),
          moduleResults.reduce(
            (sum, { moduleResult }) => sum + moduleResult.maxActivityPoints,
            0,
          ),
        ),
        lastRun: formatLastRun(
          latestDate(
            moduleResults.flatMap(({ moduleResult, testResult }) => [
              moduleResult.lastRun,
              testResult?.lastRun,
            ]),
          ),
        ),
      };

      return [studentRow, ...moduleRows];
    });
  }, [course, classStudents, intl]);

  const columns = [
    { title: <FormattedMessage id="Name" />, name: "name" },
    { title: <FormattedMessage id="Status" />, name: "status" },
    { title: <FormattedMessage id="PercentDone" />, name: "donePercentage" },
    { title: <FormattedMessage id="ModuleTestScore" />, name: "score" },
    {
      title: <FormattedMessage id="NumberOfAttempts" />,
      name: "nbrOfAttempts",
    },
    { title: <FormattedMessage id="ActivityPoints" />, name: "activityPoints" },
    { title: <FormattedMessage id="LastRun" />, name: "lastRun" },
  ];

  const columnExtensions = [
    { columnName: "name", width: 280 },
    { columnName: "status", width: 190 },
    { columnName: "donePercentage", width: 130 },
    { columnName: "score", width: 150 },
    { columnName: "nbrOfAttempts", width: 150 },
    { columnName: "activityPoints", width: 160 },
    { columnName: "lastRun", width: 200 },
  ];

  return (
    <ResultTable
      level="Class"
      tableData={tableData}
      columns={columns}
      columnExtensions={columnExtensions}
      rightAlignedColumns={[
        "donePercentage",
        "score",
        "nbrOfAttempts",
        "activityPoints",
      ]}
      centerAlignedColumns={["status"]}
    />
  );
}
